import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import SiteChatWidget from './SiteChatWidget'

// Public footer for the ai.reright.io landing + guide. HEADLESS: the tagline, link
// columns, and legal line come from GET /api/v1/site/footer (admin/agent-editable
// via the Site Content CMS). The DEFAULT below mirrors the server default so the
// footer renders correctly before the fetch resolves or if the API is unreachable.
// The R|Я brand mark stays in code — it's identity, not content.
const DEFAULT_FOOTER = {
  tagline: 'Turn your website into AI services — a support chatbot, search, automations, and more. You only pay for what you turn on.',
  columns: [
    { header: 'Product', links: [{ label: 'Products', href: '/products' }, { label: 'Use cases', href: '/use-cases' }, { label: 'User Guide', href: '/guide' }, { label: 'Sign in', href: '/' }] },
    { header: 'Resources', links: [{ label: 'User Guide', href: '/guide' }, { label: 'reright.io', href: 'https://reright.io' }, { label: 'Contact', href: '/contact' }] },
  ],
  legal: '© 2026 ReRight · AI services for your website — ai.reright.io',
}

const asArr = (v) => (Array.isArray(v) ? v : [])
const isExternal = (href) => typeof href === 'string' && /^(https?:)?\/\//.test(href)

export default function SiteFooter() {
  const [footer, setFooter] = useState(DEFAULT_FOOTER)

  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/footer')
      .then(({ data }) => {
        if (!alive) return
        const f = data && data.footer && typeof data.footer === 'object' ? data.footer : null
        if (f) setFooter({ ...DEFAULT_FOOTER, ...f, columns: asArr(f.columns).length ? f.columns : DEFAULT_FOOTER.columns })
      })
      .catch(() => { /* keep the built-in default */ })
    return () => { alive = false }
  }, [])

  return (
    <>
    <SiteChatWidget />
    <footer className="border-t border-gray-200 bg-gray-100 mt-16">
      <div className="max-w-6xl mx-auto px-5 py-12 grid gap-8 sm:grid-cols-[1.5fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="flex items-baseline font-display font-black text-xl leading-none">
              <span className="text-gray-900">R</span><span className="mx-0.5 text-gray-300 font-normal">|</span>
              <span className="text-primary-600 inline-block" style={{ transform: 'scaleX(-1)' }}>R</span>
            </span>
            <span className="font-display font-black tracking-tight text-gray-900">RERIGHT</span>
          </div>
          <p className="text-sm text-gray-500 mt-3 max-w-xs">{footer.tagline}</p>
        </div>
        {asArr(footer.columns).map((col, ci) => (
          <div key={(col && col.header) || ci}>
            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold mb-3">{col && col.header}</p>
            <ul className="space-y-2">
              {asArr(col && col.links).map((lnk, li) => {
                const label = lnk && lnk.label
                const href = (lnk && lnk.href) || '#'
                if (!label) return null
                return (
                  <li key={label + href + li}>
                    {isExternal(href) ? (
                      <a href={href} className="text-sm text-gray-500 hover:text-gray-900">{label}</a>
                    ) : (
                      <Link to={href} className="text-sm text-gray-500 hover:text-gray-900">{label}</Link>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-5 py-4 text-xs text-gray-400">{footer.legal}</div>
      </div>
    </footer>
    </>
  )
}
